<template>
  <div id="ecommerce-checkout-demo">
    <!-- Status Banner: Loading -->
    <div
      v-if="!dataReady"
      class="status-banner"
      :style="{
        background: 'var(--pok-card)',
        border: '1px solid var(--pok-border)',
      }"
    >
      <div
        style="
          background: #333;
          height: 12px;
          width: 180px;
          border-radius: 6px;
          animation: pulse 1.5s infinite;
        "
      ></div>
    </div>
    <!-- Status Banner: Loaded -->
    <div
      v-else
      class="status-banner"
      :class="
        storeClosed || !deliveryAllowed
          ? 'status-banner--closed'
          : 'status-banner--open'
      "
    >
      <div class="status-banner__dot"></div>
      <div class="status-banner__content">
        <span v-if="!storeClosed && deliveryAllowed">
          <span class="hidden sm:inline"
            >Geöffnet &bull; Lieferzeit ca.
            {{ deliveryTime || "..." }} Min.</span
          >
          <span class="sm:hidden">Geöffnet</span>
        </span>
        <span v-else-if="storeClosed">
          Geschlossen &bull; Lieferung 11:30–21:30
        </span>
        <span v-else> Lieferung zurzeit nicht möglich </span>
        <span v-if="urgency" style="color: #ff9f43; margin-left: 8px">
          &bull; Bestellschluss 21:30!
        </span>
      </div>
      <div class="status-banner__plz-btn" @click="popupDeliveryPossible = true">
        Liefern wir zu dir?
      </div>
    </div>

    <!-- Promo + Bestseller Carousel -->
    <div
      v-if="bestsellers.length > 0"
      class="carousel"
      style="margin-bottom: 16px; position: relative"
    >
      <div
        class="carousel__arrow carousel__arrow--right hidden sm:flex"
        @click="scrollCarousel(1)"
      >
        <feather-icon icon="ChevronRightIcon" svgClasses="w-5 h-5" />
      </div>
      <div class="carousel__track" ref="carouselTrack">
        <div class="carousel__slide">
          <div class="promo-slide">
            <div class="promo-slide__circles">
              <div class="promo-slide__circle promo-slide__circle--1"></div>
              <div class="promo-slide__circle promo-slide__circle--2"></div>
            </div>
            <div class="promo-slide__content">
              <span class="promo-slide__badge">PALACE OF KEBAP</span>
              <h3 class="promo-slide__title">
                Frisch vom Spieß,<br />direkt zu dir!
              </h3>
            </div>
            <span class="promo-slide__emoji">🥙</span>
          </div>
        </div>
        <div
          v-for="bs in bestsellers"
          :key="'bs-' + bs.product._id"
          class="carousel__slide"
        >
          <div class="bs-slide" @click="openBestseller(bs)">
            <div
              v-if="bs.product.photo"
              class="bs-slide__img"
              :style="{ backgroundImage: 'url(' + bs.product.photo + ')' }"
            ></div>
            <div class="bs-slide__gradient"></div>
            <div class="bs-slide__content">
              <span class="bs-slide__badge">BESTSELLER</span>
              <h4 class="bs-slide__title">{{ bs.label }}</h4>
              <p class="bs-slide__sub">{{ formatDefaults(bs.defaults) }}</p>
              <div class="bs-slide__footer">
                <span class="bs-slide__price"
                  >{{ bs.product.price.toFixed(2) }}€</span
                >
                <span class="bs-slide__btn">Auswählen</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="carousel__dots">
        <span
          v-for="i in 1 + bestsellers.length"
          :key="'dot-' + i"
          class="carousel__dot"
          :class="{ active: carouselIdx === i - 1 }"
        ></span>
      </div>
    </div>

    <div class="stepper-steps-card">
      <form-wizard
        ref="checkoutWizard"
        color="#333"
        :title="null"
        :subtitle="null"
        :hide-buttons="true"
        errorColor="rgba(var(--vs-danger), 1)"
      >
        <!-- tab 1 content -->
        <tab-content
          title="Gerichte wählen"
          icon="feather icon-shopping-cart"
          class="mb-5"
          :before-change="isMinimumPriceReached"
        >
          <!-- IF CART HAVE ITEMS -->
          <div class="vx-row" v-if="dataReady">
            <!-- LEFT COL -->
            <div class="vx-col lg:w-2/3 w-full relative">
              <vs-tabs v-if="dataReady" color="primary">
                <vs-tab
                  v-for="category in categories"
                  :key="category._id"
                  :label="category.type"
                >
                  <div class="product-grid">
                    <item-list-view
                      v-for="(item, index) in products"
                      v-if="item.category.type === category.type"
                      :key="'product-' + index"
                      :item="item"
                      :index="index"
                      :cartItems="cartItems"
                      @onShowCartButtonOnMobile="onShowCartButtonOnMobile"
                    />
                  </div>
                </vs-tab>
              </vs-tabs>
            </div>

            <!-- RIGHT COL -->
            <div class="vx-col lg:w-1/3 w-full hidden lg:block">
              <div
                class="cart-sidebar"
                style="
                  position: sticky;
                  top: 120px;
                  background: var(--pok-card);
                  border-radius: 20px;
                  border: 0.5px solid rgba(51, 51, 51, 0.5);
                  padding: 20px;
                "
              >
                <div class="flex items-center mb-4">
                  <feather-icon
                    icon="ShoppingBagIcon"
                    svgClasses="w-5 h-5 mr-2"
                    style="color: var(--pok-text)"
                  />
                  <span
                    style="
                      color: var(--pok-text);
                      font-size: 16px;
                      font-weight: 700;
                    "
                    >Warenkorb</span
                  >
                  <span
                    v-if="cartLength > 0"
                    style="
                      color: var(--pok-text-secondary);
                      font-size: 13px;
                      margin-left: auto;
                    "
                    >{{ cartLength }} Artikel</span
                  >
                </div>

                <div
                  v-if="cartLength === 0"
                  style="text-align: center; padding: 24px 0"
                >
                  <feather-icon
                    icon="ShoppingBagIcon"
                    svgClasses="w-10 h-10"
                    style="color: var(--pok-border)"
                  />
                  <p
                    style="
                      color: var(--pok-text-muted);
                      margin-top: 12px;
                      font-size: 13px;
                    "
                  >
                    Dein Warenkorb ist leer
                  </p>
                </div>

                <div
                  v-for="(item, index) in cartItems"
                  :key="index"
                  class="cart-sidebar__item"
                >
                  <div class="flex justify-between items-start">
                    <div style="flex: 1">
                      <p
                        style="
                          color: var(--pok-text);
                          font-weight: 600;
                          font-size: 14px;
                        "
                      >
                        {{ item.title }}
                      </p>
                      <p
                        style="
                          color: #c41e2a;
                          font-weight: 800;
                          font-size: 14px;
                          margin-top: 2px;
                        "
                      >
                        {{ item.price.toFixed(2) }}€
                      </p>
                    </div>
                    <div
                      class="flex items-center"
                      style="
                        background: var(--pok-card-light);
                        border-radius: 10px;
                        padding: 2px 6px;
                      "
                    >
                      <span
                        style="
                          cursor: pointer;
                          color: var(--pok-text);
                          padding: 4px 8px;
                          font-size: 16px;
                        "
                        @click="onDecrementItem(item)"
                        >−</span
                      >
                      <span
                        style="
                          color: var(--pok-text);
                          font-weight: 700;
                          min-width: 20px;
                          text-align: center;
                        "
                        >{{ item.quantity }}</span
                      >
                      <span
                        style="
                          cursor: pointer;
                          color: var(--pok-text);
                          padding: 4px 8px;
                          font-size: 16px;
                        "
                        @click="onIncrementChange(item, item.quantity + 1)"
                        >+</span
                      >
                    </div>
                  </div>
                  <div style="margin-top: 4px">
                    <div
                      v-for="(sauce, index) in item.selectedSauces"
                      :key="'sauce-' + index"
                      class="flex justify-between mb-2"
                    >
                      <span class="text-grey"
                        >Sauce {{ index + 1 }}: {{ sauce.label }}</span
                      >
                      <span v-if="sauce.price !== 0" class="text-grey"
                        >{{ sauce.price.toFixed(2) }}€</span
                      >
                    </div>
                    <div
                      v-for="(meat, index) in item.selectedMeats"
                      :key="'meat-' + index"
                      class="flex justify-between mb-2"
                    >
                      <span class="text-grey"
                        >Fleisch {{ index + 1 }}: {{ meat.label }}</span
                      >
                      <span v-if="meat.price !== 0" class="text-grey"
                        >{{ meat.price.toFixed(2) }}€</span
                      >
                    </div>
                    <div
                      v-for="(salad, index) in item.selectedSalads"
                      :key="'salad-' + index"
                      class="flex justify-between mb-2"
                    >
                      <span class="text-grey"
                        >Salat {{ index + 1 }}: {{ salad.label }}</span
                      >
                      <span v-if="salad.price !== 0" class="text-grey"
                        >{{ salad.price.toFixed(2) }}€</span
                      >
                    </div>
                    <div
                      v-for="(extra, index) in item.selectedExtras"
                      :key="'extra-' + index"
                      class="flex justify-between mb-2"
                    >
                      <span class="text-grey"
                        >Sauce {{ index + 1 }}: {{ extra.label }}</span
                      >
                      <span v-if="extra.price !== 0" class="text-grey"
                        >{{ extra.price.toFixed(2) }}€</span
                      >
                    </div>
                    <div
                      v-if="item.selectedPommRice"
                      class="flex justify-between mb-2"
                    >
                      <span class="text-grey"
                        >Beilage: {{ item.selectedPommRice.label }}</span
                      >
                      <span
                        v-if="item.selectedPommRice.price !== 0"
                        class="text-grey"
                        >{{ item.selectedPommRice.price.toFixed(2) }}€</span
                      >
                    </div>
                    <div
                      v-if="item.selectedDrink"
                      class="flex justify-between mb-2"
                    >
                      <span class="text-grey"
                        >Getränk: {{ item.selectedDrink.label }}</span
                      >
                      <span
                        v-if="item.selectedDrink.price !== 0"
                        class="text-grey"
                        >{{ item.selectedDrink.price.toFixed(2) }}€</span
                      >
                    </div>
                  </div>
                </div>

                <div
                  style="
                    border-top: 1px solid #333;
                    margin-top: 12px;
                    padding-top: 12px;
                  "
                >
                  <div
                    v-if="minimumPriceReached > 0"
                    class="flex justify-between mb-2"
                    style="font-size: 13px"
                  >
                    <span style="color: #ea5455"
                      >Noch {{ minimumPriceReached.toFixed(2) }}€ bis
                      Mindestbestellwert</span
                    >
                    <feather-icon
                      icon="AlertCircleIcon"
                      svgClasses="w-4 h-4"
                      style="color: #ea5455"
                    />
                  </div>
                  <div
                    v-else
                    class="flex justify-between mb-2"
                    style="font-size: 13px"
                  >
                    <span style="color: #2ecc71"
                      >Mindestbestellwert erreicht</span
                    >
                    <feather-icon
                      icon="CheckCircleIcon"
                      svgClasses="w-4 h-4"
                      style="color: #2ecc71"
                    />
                  </div>

                  <div
                    class="flex justify-between mb-4"
                    style="
                      color: var(--pok-text);
                      font-weight: 800;
                      font-size: 16px;
                      margin-top: 8px;
                    "
                  >
                    <span>Gesamt</span>
                    <span>{{ getTotalPrice.toFixed(2) }}€</span>
                  </div>

                  <div
                    class="cart-order-btn"
                    :class="{ disabled: !allowAddItemToCart }"
                    @click="
                      allowAddItemToCart && $refs.checkoutWizard.nextTab()
                    "
                    style="
                      background: #c41e2a;
                      color: #fff;
                      text-align: center;
                      padding: 14px;
                      border-radius: 14px;
                      font-weight: 700;
                      font-size: 15px;
                      cursor: pointer;
                    "
                  >
                    BESTELLUNG AUFGEBEN
                  </div>
                </div>
              </div>
            </div>

            <!-- RIGHT COL FOR MOBILE -->
            <div
              class="lg:hidden fixed left-0 bottom-0 w-screen"
              v-if="cartLength > 0"
            >
              <vs-button
                type="filled"
                size="large"
                class="w-screen z-1000 w-4 opacity-80"
                @click="popupMobileCart = true"
              >
                <div class="flex-1 items-center h-full">
                  <feather-icon
                    icon="ShoppingCartIcon"
                    class="cursor-pointer mr-2"
                    :badge="cartLength"
                  />
                  <span class="items-center"
                    >WARENKORB ({{ getTotalPrice.toFixed(2) }}€)</span
                  >
                </div>
              </vs-button>
            </div>
          </div>

          <!-- IF NO ITEMS IN CART -->
          <vx-card title="Momentan sind keine Produkte verfügbar." v-else>
            <vs-button
              @click="$router.push('/apps/eCommerce/shop').catch(() => {})"
              >OK</vs-button
            >
          </vx-card>
        </tab-content>

        <!-- tab 2 content -->
        <tab-content
          title="Addresse"
          icon="feather icon-home"
          class="mb-5"
          :before-change="submitNewAddressForm"
        >
          <div class="vx-row">
            <!-- LEFT COL: NEW ADDRESS -->
            <div class="vx-col w-full">
              <vx-card
                title="Adresse hinzufügen"
                subtitle="An diese Adresse wird deine Bestellung geliefert"
                class="mb-base"
              >
                <form data-vv-scope="add-new-address" autocomplete="off">
                  <div class="vx-row">
                    <div class="vx-col sm:w-1/2 w-full">
                      <vs-input
                        v-validate="'required'"
                        data-vv-as="field"
                        name="fullName"
                        label="Name:"
                        v-model="fullName"
                        class="w-full mt-5"
                      />
                      <span
                        v-show="errors.has('add-new-address.fullName')"
                        class="text-danger"
                        >Bitte gib einen Namen an.</span
                      >
                    </div>

                    <div class="vx-col sm:w-1/2 w-full">
                      <vs-input
                        v-validate="'required'"
                        name="mobileNum"
                        label="Handynummer:"
                        v-model="mobileNum"
                        class="w-full mt-5"
                      />
                      <span
                        v-show="errors.has('add-new-address.mobileNum')"
                        class="text-danger"
                        >Bitte gib deine Telefonnummer an.</span
                      >
                    </div>
                  </div>

                  <div class="vx-row">
                    <div class="vx-col sm:w-3/4 w-full">
                      <vs-input
                        v-validate="'required'"
                        name="street"
                        label="Straße:"
                        v-model="street"
                        class="w-full mt-5"
                      />
                      <span
                        v-show="errors.has('add-new-address.street')"
                        class="text-danger"
                        >Bitte gib deine Straße an.</span
                      >
                    </div>

                    <div class="vx-col sm:w-1/4 w-full">
                      <vs-input
                        v-validate="'required'"
                        name="streetNumber"
                        label="Hausnummer:"
                        v-model="streetNumber"
                        class="w-full mt-5"
                      />
                      <span
                        v-show="errors.has('add-new-address.streetNumber')"
                        class="text-danger"
                        >Bitte gib deine Hausnummer an.</span
                      >
                    </div>
                  </div>

                  <div class="vx-row">
                    <div class="vx-col sm:w-1/2 w-full">
                      <vs-input
                        v-validate="'required'"
                        name="city"
                        label="Ort:"
                        v-model="city"
                        class="w-full mt-5"
                      />
                      <span
                        v-show="errors.has('add-new-address.city')"
                        class="text-danger"
                        >Bitte gib deinen Ort an.</span
                      >
                    </div>

                    <div class="vx-col sm:w-1/2 w-full">
                      <vs-input
                        v-validate="'postalCode|required|min:3|max:6|numeric'"
                        name="postalCode"
                        label="Postleitzahl:"
                        v-model="postalCode"
                        class="w-full mt-5"
                      />
                      <span
                        v-show="errors.has('add-new-address.postalCode')"
                        class="text-danger"
                        >Wir liefern derzeit leider nur innerhalb von
                        Düsseldorf</span
                      >
                    </div>
                  </div>

                  <div class="vx-row">
                    <div class="vx-col w-full">
                      <vs-textarea
                        name="notes"
                        label="Nachricht an Lieferant:"
                        v-model="notes"
                        class="w-full mt-5"
                        height="200px"
                      />
                    </div>
                  </div>
                  <div class="vx-row">
                    <div class="vx-col w-full flex">
                      <vs-checkbox
                        v-validate="'required'"
                        name="isTermsConditionAccepted"
                        v-model="isTermsConditionAccepted"
                      >
                        AGBs akzeptieren
                      </vs-checkbox>
                      <p
                        class="ml-4 cursor-pointer"
                        @click="
                          showTermsAndConditions = !showTermsAndConditions
                        "
                      >
                        (AGBs lesen)
                      </p>
                    </div>
                    <div
                      class="text-danger vx-col w-full"
                      v-if="showIsTermsConditionAcceptedError"
                    >
                      Bitte akzeptiere die Allgemeinen Geschäftsbedingungen.
                    </div>
                  </div>
                </form>
              </vx-card>
            </div>
          </div>
          <div style="height: 100px"></div>

          <!-- Fixed bottom buttons -->
          <div class="fixed-bottom-btn">
            <div
              class="fixed-bottom-btn__back"
              @click="$refs.checkoutWizard.prevTab()"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                svgClasses="w-5 h-5 mr-2"
              />Zurück
            </div>
            <div class="fixed-bottom-btn__inner" @click="submitNewAddressForm">
              <span class="hidden sm:inline">Weiter zur Bezahlung</span
              ><span class="sm:hidden">Weiter</span
              ><feather-icon icon="ArrowRightIcon" svgClasses="w-5 h-5 ml-2" />
            </div>
          </div>
        </tab-content>

        <!-- tab 3 content -->
        <tab-content
          title="Bezahlen"
          icon="feather icon-credit-card"
          class="mb-5"
        >
          <div class="vx-row">
            <!-- LEFT COL: PAYMENT OPTIONS -->
            <div class="vx-col lg:w-2/3 w-full">
              <vx-card
                title="Deine Bestellung"
                subtitle="Überprüfe nochmals deine Bestellung"
                class="mb-base"
              >
                <div v-for="(item, index) in cartItems" :key="index">
                  <div class="flex justify-between font-semibold">
                    <span
                      >{{ item.title }}
                      <span class="text-primary"
                        >&nbsp; &nbsp; &nbsp; X {{ item.quantity }}</span
                      >
                    </span>
                    <span>{{ item.price.toFixed(2) }}€</span>
                  </div>

                  <div
                    v-for="(sauce, index) in item.selectedSauces"
                    :key="'sauce-' + index"
                    class="flex justify-between mb-2"
                  >
                    <span class="text-grey"
                      >Sauce {{ index + 1 }}: {{ sauce.label }}</span
                    >
                    <span v-if="sauce.price !== 0" class="text-grey"
                      >{{ sauce.price.toFixed(2) }}€</span
                    >
                  </div>
                  <div
                    v-for="(meat, index) in item.selectedMeats"
                    :key="'meat-' + index"
                    class="flex justify-between mb-2"
                  >
                    <span class="text-grey"
                      >Fleisch {{ index + 1 }}: {{ meat.label }}</span
                    >
                    <span v-if="meat.price !== 0" class="text-grey"
                      >{{ meat.price.toFixed(2) }}€</span
                    >
                  </div>
                  <div
                    v-for="(salad, index) in item.selectedSalads"
                    :key="'salad-' + index"
                    class="flex justify-between mb-2"
                  >
                    <span class="text-grey"
                      >Salat {{ index + 1 }}: {{ salad.label }}</span
                    >
                    <span v-if="salad.price !== 0" class="text-grey"
                      >{{ salad.price.toFixed(2) }}€</span
                    >
                  </div>
                  <div
                    v-for="(extra, index) in item.selectedExtras"
                    :key="'extra-' + index"
                    class="flex justify-between mb-2"
                  >
                    <span class="text-grey"
                      >Sauce {{ index + 1 }}: {{ extra.label }}</span
                    >
                    <span v-if="extra.price !== 0" class="text-grey"
                      >{{ extra.price.toFixed(2) }}€</span
                    >
                  </div>
                  <div
                    v-if="item.selectedPommRice"
                    class="flex justify-between mb-2"
                  >
                    <span class="text-grey"
                      >Beilage: {{ item.selectedPommRice.label }}</span
                    >
                    <span
                      v-if="item.selectedPommRice.price !== 0"
                      class="text-grey"
                      >{{ item.selectedPommRice.price.toFixed(2) }}€</span
                    >
                  </div>
                  <div
                    v-if="item.selectedDrink"
                    class="flex justify-between mb-2"
                  >
                    <span class="text-grey"
                      >Getränk: {{ item.selectedDrink.label }}</span
                    >
                    <span
                      v-if="item.selectedDrink.price !== 0"
                      class="text-grey"
                      >{{ item.selectedDrink.price.toFixed(2) }}€</span
                    >
                  </div>
                </div>

                <vs-divider />

                <div class="flex justify-between font-semibold mb-3">
                  <span>Insgesamt</span>
                  <span>{{ getTotalPrice.toFixed(2) }}€</span>
                </div>
              </vx-card>
            </div>

            <!-- RIGHT COL: PRICE -->
            <div class="vx-col lg:w-1/3 w-full">
              <vx-card
                v-if="!orderCompleted"
                title="Bezahlung"
                subtitle="Wähle eine Zahlungsmethode aus"
                class="mb-base w-full"
              >
                <div ref="paypal"></div>
                <div>
                  <vs-button
                    text-color="#626262"
                    color="#eee"
                    class="w-full font-bold"
                    @click="orderAndPayInCash"
                    >Bar Bezahlen</vs-button
                  >
                </div>
              </vx-card>
              <div style="height: 100px"></div>
              <div
                v-if="orderCompleted"
                style="text-align: center; padding: 40px 20px"
              >
                <div
                  style="
                    background: rgba(46, 204, 113, 0.15);
                    border: 1px solid rgba(46, 204, 113, 0.3);
                    border-radius: 20px;
                    padding: 32px;
                    max-width: 500px;
                    margin: 0 auto;
                  "
                >
                  <div
                    style="
                      background: rgba(46, 204, 113, 0.2);
                      width: 64px;
                      height: 64px;
                      border-radius: 50%;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      margin: 0 auto 16px;
                    "
                  >
                    <feather-icon
                      icon="CheckIcon"
                      svgClasses="w-8 h-8"
                      style="color: #2ecc71"
                    />
                  </div>
                  <h3
                    style="
                      color: #2ecc71;
                      font-weight: 800;
                      font-size: 22px;
                      margin-bottom: 8px;
                    "
                  >
                    Bestellung erfolgreich!
                  </h3>
                  <p
                    style="
                      color: var(--pok-text-secondary);
                      font-size: 14px;
                      line-height: 1.5;
                    "
                  >
                    Deine Bestellung ist bei uns eingegangen und ist bald auf
                    dem Weg zu dir. Vielen Dank für dein Vertrauen!
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="fixed-bottom-btn">
            <div class="fixed-bottom-btn__inner" @click="goHome">
              Zurück zur Startseite
            </div>
          </div>
        </tab-content>
      </form-wizard>
    </div>
    <vs-popup
      fullscreen
      class="holamundo"
      title="Warenkorb"
      :active.sync="popupMobileCart"
    >
      <vx-card>
        <div v-for="(item, index) in cartItems" :key="index">
          <div class="flex justify-between font-semibold">
            <span>{{ item.title }}</span>
            <span>{{ item.price.toFixed(2) }}€</span>
          </div>
          <div class="flex justify-start items-center">
            <vs-input-number
              color="dark"
              min="1"
              max="10"
              :value="item.quantity"
              class="inline-flex mr-4 cart-qty-input"
              @input="onIncrementChange(item, $event)"
            />
            <vs-button
              color="danger"
              type="filled"
              icon-pack="feather"
              icon="icon-trash-2"
              @click="onRemoveItemFromCart(item)"
              class="cart-delete-btn"
            ></vs-button>
          </div>

          <div
            v-for="(sauce, index) in item.selectedSauces"
            :key="'sauce-' + index"
            class="flex justify-between mb-2"
          >
            <span class="text-grey"
              >Sauce {{ index + 1 }}: {{ sauce.label }}</span
            >
            <span v-if="sauce.price !== 0" class="text-grey"
              >{{ sauce.price.toFixed(2) }}€</span
            >
          </div>
          <div
            v-for="(meat, index) in item.selectedMeats"
            :key="'meat-' + index"
            class="flex justify-between mb-2"
          >
            <span class="text-grey"
              >Fleisch {{ index + 1 }}: {{ meat.label }}</span
            >
            <span v-if="meat.price !== 0" class="text-grey"
              >{{ meat.price.toFixed(2) }}€</span
            >
          </div>
          <div
            v-for="(salad, index) in item.selectedSalads"
            :key="'salad-' + index"
            class="flex justify-between mb-2"
          >
            <span class="text-grey"
              >Salat {{ index + 1 }}: {{ salad.label }}</span
            >
            <span v-if="salad.price !== 0" class="text-grey"
              >{{ salad.price.toFixed(2) }}€</span
            >
          </div>
          <div
            v-for="(extra, index) in item.selectedExtras"
            :key="'extra-' + index"
            class="flex justify-between mb-2"
          >
            <span class="text-grey"
              >Sauce {{ index + 1 }}: {{ extra.label }}</span
            >
            <span v-if="extra.price !== 0" class="text-grey"
              >{{ extra.price.toFixed(2) }}€</span
            >
          </div>
          <div v-if="item.selectedPommRice" class="flex justify-between mb-2">
            <span class="text-grey"
              >Beilage: {{ item.selectedPommRice.label }}</span
            >
            <span v-if="item.selectedPommRice.price !== 0" class="text-grey"
              >{{ item.selectedPommRice.price.toFixed(2) }}€</span
            >
          </div>
          <div v-if="item.selectedDrink" class="flex justify-between mb-2">
            <span class="text-grey"
              >Getränk: {{ item.selectedDrink.label }}</span
            >
            <span v-if="item.selectedDrink.price !== 0" class="text-grey"
              >{{ item.selectedDrink.price.toFixed(2) }}€</span
            >
          </div>
        </div>

        <vs-divider />

        <div v-if="minimumPriceReached > 0" class="flex justify-between mb-2">
          <span class="text-danger"
            >Fehlender Betrag für Mindestbestellwert</span
          >
          <span class="text-danger">{{ minimumPriceReached.toFixed(2) }}€</span>
        </div>
        <div v-if="minimumPriceReached <= 0" class="flex justify-between mb-2">
          <span class="text-success"
            >Du hast den Mindestbestellwert erreicht</span
          >
        </div>

        <vs-divider />

        <div class="flex justify-between font-semibold mb-3">
          <span>Insgesamt</span>
          <span>{{ getTotalPrice.toFixed(2) }}€</span>
        </div>

        <vs-button
          size="large"
          :disabled="!allowAddItemToCart"
          class="w-full z-50"
          @click="
            $refs.checkoutWizard.nextTab();
            popupMobileCart = false;
          "
          >BESTELLEN</vs-button
        >
      </vx-card>
    </vs-popup>

    <!-- POPUP PAYMENT FAILED -->
    <vs-popup title="Bezahlung fehlgeschlagen" :active.sync="isPaymentFailed">
      <p>
        Es tut uns leid, wir können deine Bestellung derzeit nicht annehmen.
      </p>
    </vs-popup>

    <!-- PLZ CHECK DRAWER -->
    <div>
      <div
        v-if="popupDeliveryPossible"
        class="plz-overlay"
        @click.stop="popupDeliveryPossible = false"
      ></div>
      <div
        v-if="popupDeliveryPossible"
        class="plz-drawer"
        @click.stop
        :class="{ 'plz-drawer--mobile': windowWidth < 768 }"
      >
        <div
          style="
            background: linear-gradient(135deg, #c41e2a, #8b1520);
            padding: 24px;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            height: 140px;
            flex-shrink: 0;
            position: relative;
          "
        >
          <div
            style="
              position: absolute;
              top: 16px;
              right: 16px;
              background: rgba(0, 0, 0, 0.3);
              border-radius: 50%;
              padding: 8px;
              cursor: pointer;
            "
            @click="popupDeliveryPossible = false"
          >
            <feather-icon
              icon="XIcon"
              svgClasses="w-5 h-5"
              style="color: #fff"
            />
          </div>
          <h3 style="color: #fff; font-size: 22px; font-weight: 800">
            Liefern wir zu dir?
          </h3>
          <p style="color: rgba(255, 255, 255, 0.7); font-size: 14px">
            Liefergebiet: Düsseldorf
          </p>
        </div>
        <div style="padding: 20px; flex: 1">
          <div class="auth-input" style="margin-bottom: 16px">
            <feather-icon icon="MapPinIcon" svgClasses="w-5 h-5" />
            <input
              type="text"
              placeholder="Deine Postleitzahl"
              v-model="temporaryPostalCode"
              @keyup.enter="onCheckPostalCode"
            />
          </div>
          <div
            v-if="deliveryPossible === 'possible'"
            style="
              background: rgba(46, 204, 113, 0.15);
              border: 1px solid rgba(46, 204, 113, 0.3);
              border-radius: 14px;
              padding: 16px;
              margin-bottom: 16px;
              display: flex;
              align-items: center;
            "
          >
            <feather-icon
              icon="CheckCircleIcon"
              svgClasses="w-5 h-5 mr-3"
              style="color: #2ecc71"
            />
            <span style="color: #2ecc71; font-weight: 600"
              >Wir liefern an deine Adresse!</span
            >
          </div>
          <div
            v-if="deliveryPossible === 'impossible'"
            style="
              background: rgba(234, 84, 85, 0.15);
              border: 1px solid rgba(234, 84, 85, 0.3);
              border-radius: 14px;
              padding: 16px;
              margin-bottom: 16px;
              display: flex;
              align-items: center;
            "
          >
            <feather-icon
              icon="XCircleIcon"
              svgClasses="w-5 h-5 mr-3"
              style="color: #ea5455"
            />
            <span style="color: #ea5455; font-weight: 600"
              >Leider nicht in deinem Gebiet.</span
            >
          </div>
        </div>
        <div style="padding: 12px 20px 24px">
          <div
            @click="onCheckPostalCode"
            style="
              background: #c41e2a;
              color: #fff;
              text-align: center;
              padding: 16px;
              border-radius: 14px;
              font-weight: 700;
              font-size: 16px;
              cursor: pointer;
            "
          >
            Prüfen
          </div>
        </div>
      </div>
    </div>

    <!-- POPUP TO SHOW TERMS AND CONDITIONS -->
    <vs-popup
      fullscreen
      class="p-4"
      title="Allgemeine Geschäftsbedingungen"
      :active.sync="showTermsAndConditions"
    >
      <terms-and-conditions></terms-and-conditions>
    </vs-popup>

    <!-- PRODUCT INGREDIENT DRAWER (rendered at root level, not inside cards) -->
    <product-modal
      v-if="drawerItem"
      :item="drawerItem"
      :active.sync="showDrawer"
      :cartItems="cartItems"
      @onAddedToCart="onShowCartButtonOnMobile"
    />
  </div>
</template>

<script>
import { Validator } from "vee-validate";
import { FormWizard, TabContent } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { uuid } from "vue-uuid";
import { mapActions } from "vuex";
import { cartItemsToOrderItems } from "../../assets/utils/custom";
import axios from "../../axios";
const ItemListView = () => import("./components/ItemListView.vue");
const ProductModal = () => import("./components/ProductModal.vue");

Validator.extend("postalCode", {
  // Custom validation message
  getMessage: () => "Wir liefern derzeit leider nur in Düsseldorf.",
  // Custom validation rule
  validate: (value) =>
    new Promise((resolve) => {
      resolve({
        valid: [
          "40213",
          "40468",
          "40476",
          "40477",
          "40479",
          "40237",
          "40239",
          "40470",
          "40233",
          "40235",
          "40210",
          "40212",
          "40215",
          "40474",
          "40472",
          "40547",
          "40227",
          "40545",
          "40211",
          "40217",
          "40219",
        ].includes(value),
      });
    }),
});

export default {
  data() {
    return {
      temporaryPostalCode: "",
      validPostalCodes: [
        "40213",
        "40468",
        "40476",
        "40477",
        "40479",
        "40237",
        "40239",
        "40470",
        "40233",
        "40235",
        "40210",
        "40212",
        "40215",
        "40474",
        "40472",
        "40547",
        "40227",
        "40545",
        "40211",
        "40217",
        "40219",
      ],
      deliveryPossible: "undetermined",
      popupDeliveryPossible: false,

      deliveryTime: "",
      deliveryAllowed: true,
      storeClosed: false,
      urgency: false,

      dataReady: false,
      drawerItem: null,
      showDrawer: false,
      bestsellers: [],
      carouselIdx: 0,
      showCartButtonOnMobile: false,
      popupMobileCart: false,

      // TAB 2
      fullName: "",
      mobileNum: "",
      street: "",
      streetNumber: "",
      city: "",
      postalCode: "",
      notes: "",
      isTermsConditionAccepted: false,
      showTermsAndConditions: false,
      showIsTermsConditionAcceptedError: false,

      autocomplete: null,

      // TAB 3
      paymentMethod: "paypal",
      cvv: "",
      orderCompleted: false,
      showPopupOrderCompleted: false,
      isPaymentFailed: false,
      orderID: "",
    };
  },
  computed: {
    products() {
      return this.$store.state.shop.products;
    },
    categories() {
      return this.$store.state.shop.categories;
    },
    cartItems() {
      return this.$store.state.shop.cartItems.slice().reverse();
    },
    cartLength() {
      return this.$store.getters["shop/getCartLength"];
    },
    isInWishList() {
      return (itemId) => this.$store.getters["shop/isInWishList"](itemId);
    },
    getTotalPrice() {
      return this.$store.getters["shop/cartTotalPrice"];
    },
    minimumPriceReached() {
      return this.$store.getters["shop/minimumPriceReached"];
    },
    shippingAddress() {
      return this.$store.getters["shop/getShippingAddress"];
    },
    user() {
      return this.$store.getters["auth/user"];
    },
    authenticated() {
      return this.$store.getters["auth/authenticated"];
    },
    windowWidth() {
      return this.$store.state.windowWidth || window.innerWidth;
    },
    plzChecked() {
      return (
        this.deliveryPossible === "possible" ||
        this.deliveryPossible === "impossible"
      );
    },
    allowAddItemToCart() {
      return (
        this.deliveryAllowed &&
        this.minimumPriceReached <= 0 &&
        !this.storeClosed
      );
    },
  },
  beforeDestroy() {
    if (this._stepperObserver) this._stepperObserver.disconnect();
  },
  watch: {
    bestsellers: function () {
      this.initCarouselDots();
    },
  },
  async mounted() {
    this.$root.$on("openMobileCart", () => {
      this.popupMobileCart = true;
    });
    this.$root.$on("openProductDrawer", (item) => {
      this.drawerItem = item;
      this.showDrawer = true;
    });

    // MutationObserver to fix stepper colors after every DOM change
    this.$nextTick(() => {
      var container = document.querySelector(".stepper-steps-card");
      if (container) {
        var fix = function () {
          var s = getComputedStyle(document.documentElement);
          var cardColor = s.getPropertyValue("--pok-card").trim();
          var borderColor = s.getPropertyValue("--pok-border").trim();
          var mutedColor = s.getPropertyValue("--pok-text-muted").trim();
          var lis = container.querySelectorAll(".wizard-nav-pills li");
          var activeIdx = -1;
          for (var i = 0; i < lis.length; i++) {
            if (lis[i].classList.contains("active")) {
              activeIdx = i;
              break;
            }
          }
          lis.forEach(function (li, i) {
            var circle = li.querySelector(".wizard-icon-circle");
            var iconContainer = li.querySelector(".wizard-icon-container");
            var allIcons = li.querySelectorAll(".wizard-icon");
            if (!circle) return;
            if (i === activeIdx) {
              circle.style.cssText =
                "border-color:#C41E2A!important;background:rgba(196,30,42,0.15)!important";
              if (iconContainer)
                iconContainer.style.cssText =
                  "background-color:#C41E2A!important";
              allIcons.forEach(function (ic) {
                ic.style.cssText = "color:#fff!important";
              });
            } else if (i < activeIdx) {
              circle.style.cssText =
                "border-color:#2ECC71!important;background:rgba(46,204,113,0.15)!important";
              if (iconContainer) iconContainer.style.cssText = "";
              allIcons.forEach(function (ic) {
                ic.style.cssText = "color:#2ECC71!important";
              });
            } else {
              circle.style.cssText =
                "border-color:" +
                borderColor +
                "!important;background:" +
                cardColor +
                "!important";
              if (iconContainer) iconContainer.style.cssText = "";
              allIcons.forEach(function (ic) {
                ic.style.cssText = "color:" + mutedColor + "!important";
              });
            }
          });
          // Progress bar
          var bar = container.querySelector(".wizard-progress-bar");
          if (bar) bar.style.cssText = "background-color:#2ECC71!important";
        };
        fix();
        var fixing = false;
        this._stepperObserver = new MutationObserver(function () {
          if (fixing) return;
          fixing = true;
          requestAnimationFrame(function () {
            fix();
            fixing = false;
          });
        });
        this._stepperObserver.observe(container, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ["class"],
        });
      }
    });

    this.$vs.loading();

    // get products and categories
    await this.getProducts();
    await this.getCategories();

    // check if delivery is possible at the moment
    const today = new Date();
    const timeInHours = today.getHours();
    const timeInMinutes = today.getMinutes();

    if (timeInHours < 11 || timeInHours > 21) {
      this.storeClosed = true;
    } else {
      if (timeInHours === 11 && timeInMinutes < 30) {
        this.storeClosed = true;
      }
      if (timeInHours === 21 && timeInMinutes > 30) {
        this.storeClosed = true;
      }
      if (timeInHours === 21 && timeInMinutes < 30) {
        this.urgency = true;
      }
    }

    axios.get("/api/delivery-time").then((res) => {
      this.deliveryTime = res.data.deliveryTime.time;
      this.deliveryAllowed = res.data.deliveryTime.isPossible;
    });

    this.dataReady = true;

    /* const options = {
      componentRestrictions: {country: "de"}
    } */
    /* this.autocomplete = new google.maps.places.Autocomplete(
      document.getElementById("autocomplete"),
      options
    )

    this.autocomplete.setFields(["address_component"]);
    this.autocomplete.addListener("place_changed", this.fillInAddress); */

    // Fetch bestsellers
    var self = this;
    axios
      .get("/api/products/bestsellers")
      .then(function (res) {
        if (res.data && res.data.success) {
          self.bestsellers = res.data.bestsellers;
        }
      })
      .catch(function () {});

    this.$vs.loading.close();

    this.fetchShippingAddress()
      .then((res) => {
        // returns 400 when not logged in
        // returns empty array if logged in but no address provided
        // returns address as first element in array
        if (res.data.address.length === 1) {
          this.fullName = this.user.name;
          let address = res.data.address[0];
          this.mobileNum = address.phone;
          this.street = address.street;
          this.streetNumber = address.streetNumber;
          this.postalCode = address.zipCode;
          this.city = address.city;
          this.notes = address.deliveryInstructions;
        }
      })
      .catch((error) => {
        console.log(error);
      });

    // load paypal script
    const { data } = await axios.get("/api/paypal-v2/client");
    console.log("clientID", data.client_id);
    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${data.client_id}&currency=EUR&locale=de_DE`;
    script.addEventListener("load", this.setLoaded);
    document.body.appendChild(script);
  },
  methods: {
    //GENERAL
    ...mapActions({
      getProducts: "shop/getProducts",
      getCategories: "shop/getCategories",
      saveShippingAddress: "shop/saveShippingAddress",
      savePaymentMethod: "shop/savePaymentMethod",
      submitOrder: "shop/submitOrder",
      onPaymentSuccess: "shop/onPaymentSuccess",
      fetchShippingAddress: "shop/fetchShippingAddress",
    }),
    scrollCarousel(dir) {
      var track = this.$refs.carouselTrack;
      if (!track || !track.children[0]) return;
      var slideW = track.children[0].offsetWidth + 12;
      track.scrollBy({ left: dir * slideW, behavior: "smooth" });
    },
    initCarouselDots() {
      var self = this;
      this.$nextTick(function () {
        var track = self.$refs.carouselTrack;
        if (!track) return;
        var slides = track.querySelectorAll(".carousel__slide");
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                var idx = Array.from(slides).indexOf(entry.target);
                if (idx >= 0) self.carouselIdx = idx;
              }
            });
          },
          { root: track, threshold: 0.6 }
        );
        slides.forEach(function (s) {
          observer.observe(s);
        });
      });
    },
    formatDefaults(defaults) {
      var parts = [];
      if (defaults.meats && defaults.meats.length)
        parts.push(defaults.meats[0].name);
      if (defaults.sauces && defaults.sauces.length)
        parts.push(defaults.sauces[0].name);
      if (defaults.salads && defaults.salads.length)
        parts.push(defaults.salads[0].name);
      return parts.join(" · ");
    },
    goHome() {
      window.location.href = "/";
    },
    openBestseller(bs) {
      this.drawerItem = bs.product;
      this.showDrawer = true;
    },
    onCheckPostalCode() {
      this.deliveryPossible = this.validPostalCodes.includes(
        this.temporaryPostalCode
      )
        ? "possible"
        : "impossible";
    },

    // TAB 1

    isMinimumPriceReached() {
      if (this.minimumPriceReached > 0) {
        this.$vs.notify({
          title: "Fehler",
          text: "Deine Bestellung muss mindestens 15€ wert sein",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
          position: "top-center",
        });
        return false;
      } else {
        return true;
      }
    },
    onIncrementChange(cartItem, newValue) {
      this.$store.commit("shop/UPDATE_ITEM_QUANTITY", {
        cartItemID: cartItem.cartItemID,
        newValue: newValue,
      });
    },
    onDecrementItem(item) {
      if (item.quantity > 1) {
        this.onIncrementChange(item, item.quantity - 1);
      } else {
        this.onRemoveItemFromCart(item);
      }
    },
    onRemoveItemFromCart(cartItem) {
      this.$store.commit("shop/REMOVE_ITEM_FROM_CART", {
        cartItemID: cartItem.cartItemID,
      });
    },
    onShowCartButtonOnMobile() {
      this.showCartButtonOnMobile = true;
    },

    // TAB 2
    fillInAddress() {
      // Get the place details from the autocomplete object.
      const place = this.autocomplete.getPlace();

      // Get each component of the address from the place details,
      // and then fill-in the corresponding field on the form.
      for (let component of place.address_components) {
        const addressType = component.types[0];
        switch (addressType) {
          case "postal_code":
            this.postalCode = component.long_name;
            break;
          case "route":
            this.street = component.long_name;
            break;
          case "street_number":
            this.streetNumber = component.long_name;
            break;
          case "sublocality_level_1":
            this.city = component.long_name;
            break;
          case "locality":
            this.city = component.long_name;
            break;
          default:
            break;
        }
      }
    },
    submitNewAddressForm() {
      //this.fillInAddress();
      return new Promise((resolve, reject) => {
        this.$validator.validateAll("add-new-address").then((result) => {
          if (result && this.isTermsConditionAccepted) {
            this.saveShippingAddress({
              name: this.fullName,
              phone: this.mobileNum,
              street: this.street,
              streetNumber: this.streetNumber,
              city: this.city,
              zipCode: this.postalCode,
              deliveryInstructions: this.notes,
            });
            resolve(true);
            this.$refs.checkoutWizard.nextTab();
          } else {
            if (!this.isTermsConditionAccepted)
              this.showIsTermsConditionAcceptedError = true;
            this.$vs.notify({
              title: "Fehler",
              text: "Fülle alle Felder korrekt aus",
              color: "danger",
              iconPack: "feather",
              icon: "icon-alert-circle",
              position: "top-center",
            });
            reject("correct all values");
          }
        });
      });
    },

    // TAB 3
    setLoaded() {
      this.orderID = uuid.v1();
      console.log(this.orderID);
      let that = this;
      window.paypal
        .Buttons({
          async createOrder() {
            return fetch(
              `${process.env.VUE_APP_BACKEND_URL}/api/paypal-v2/create-order`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  orderItems: cartItemsToOrderItems(
                    that.$store.state.shop.cartItems
                  ),
                  shippingAddress: that.$store.state.shop.shippingAddress,
                }),
              }
            )
              .then((res) => {
                if (res.ok) return res.json();
                return res.json().then((json) => Promise.reject(json));
              })
              .then((res2) => res2.id)
              .catch((e) => {
                console.log(e);
                /*    this.$toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: "Fehler",
                      icon: "icon-alert-circle",
                      text: "Die Bestellung konnte nicht gesendet werden.",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 4000,
                  }
                ); */
              });
          },
          onApprove: function (data, actions) {
            // This function captures the funds from the transaction.
            return actions.order.capture().then(function () {
              // This function shows a transaction success message to your buyer.

              // update order status to paid
              that.onPaymentSuccess(that.orderID);
              that.orderCompleted = true;
              that.showPopupOrderCompleted = true;

              //that.$router.go();
              /*  that.$vs.loading.close() */
            });
          },
          onCancel: function (data) {
            that.orderID = uuid.v1();
            console.log(that.orderID);
          },
        })
        .render(this.$refs.paypal);
    },
    orderAndPayInCash() {
      let orderID = uuid.v1();
      this.savePaymentMethod("bar");
      this.submitOrder(orderID)
        .then(() => {
          this.orderCompleted = true;
          this.showPopupOrderCompleted = true;
        })
        .catch(() => {
          this.$vs.notify({
            title: "Fehler",
            text: "Die Bestellung konnte nicht gesendet werden.",
            color: "danger",
            iconPack: "feather",
            icon: "icon-alert-circle",
            position: "top-center",
          });
        });
    },
  },
  components: {
    ItemListView,
    ProductModal,
    FormWizard,
    TabContent,
    VuePerfectScrollbar,
  },
};
</script>

<style lang="scss" scoped>
#ecommerce-checkout-demo {
  ul {
    margin: 0;
  }

  .item-view-primary-action-btn {
    color: #2c2c2c !important;
    background-color: #f6f6f6;
  }

  .item-name {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
  }

  .stepper-steps-card {
    margin-top: 16px;
    margin-bottom: 16px;
  }

  .fixed-bottom-btn {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 50000;
    padding: 12px 16px 24px;
    background: var(--pok-surface);
    border-top: 0.5px solid rgba(51, 51, 51, 0.3);

    display: flex;
    justify-content: space-between;
    gap: 12px;

    &__back {
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--pok-card-light);
      color: var(--pok-text);
      padding: 16px 24px;
      border-radius: 14px;
      font-weight: 600;
      font-size: 15px;
      cursor: pointer;
      border: 1px solid var(--pok-border);

      &:hover {
        background: var(--pok-border);
      }
    }

    &__inner {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #c41e2a;
      color: #fff;
      padding: 16px 24px;
      border-radius: 14px;
      font-weight: 700;
      font-size: 16px;
      cursor: pointer;

      &:hover {
        opacity: 0.9;
      }
    }
  }

  .vue-form-wizard {
    padding-bottom: 0;

    ::v-deep .wizard-header {
      padding: 0;
    }

    ::v-deep .wizard-tab-content {
      padding-right: 0;
      padding-left: 0;
      padding-bottom: 0;

      .wizard-tab-container {
        margin-bottom: 0 !important;
      }
    }

    // Spacing between category tabs and product list
    ::v-deep .con-ul-tabs {
      margin-bottom: 1rem;
    }

    ::v-deep .con-slot-tabs {
      padding-top: 0.5rem;
    }

    // Desktop: keep separate scroll for product list
    @media (min-width: 768px) {
      ::v-deep .con-slot-tabs {
        max-height: calc(100dvh - 340px);
        overflow-y: auto !important;
      }
    }
  }

  .cart-order-btn {
    &.disabled {
      background: var(--pok-border) !important;
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .cart-sidebar__item {
    background: var(--pok-bg);
    border-radius: 14px;
    padding: 12px;
    margin-bottom: 8px;
  }

  .product-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding-bottom: 80px;

    @media (min-width: 480px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (min-width: 1200px) {
      grid-template-columns: repeat(4, 1fr);
    }
  }
}
</style>

<style lang="scss">
.stepper-steps-card {
  .wizard-icon-circle {
    background: var(--pok-card) !important;
    border-color: var(--pok-border) !important;
  }

  .wizard-icon-circle.active {
    border-color: #c41e2a !important;
    background: rgba(196, 30, 42, 0.15) !important;

    .wizard-icon {
      color: #c41e2a !important;
    }
  }

  .wizard-icon-circle.checked:not(.active) {
    border-color: #2ecc71 !important;
    background: rgba(46, 204, 113, 0.15) !important;

    .wizard-icon {
      color: #2ecc71 !important;
    }
  }

  .wizard-progress-bar {
    background: #2ecc71 !important;
  }

  .wizard-progress-with-circle {
    background: var(--pok-border) !important;

    @media (max-width: 576px) {
      display: none;
    }
  }

  .wizard-nav.wizard-nav-pills {
    background: var(--pok-card);
    border-radius: 20px;
    border: 0.5px solid rgba(51, 51, 51, 0.5);
    padding: 16px;
    margin-bottom: 16px;

    @media (max-width: 576px) {
      display: none;
    }
  }

  .stepTitle {
    color: var(--pok-text-secondary) !important;
  }

  .active .stepTitle {
    color: var(--pok-text) !important;
  }

  .checked .stepTitle {
    color: #2ecc71 !important;
  }
}

.status-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 14px;
  margin-top: 8px;

  @media (max-width: 576px) {
    margin-top: 0;
  }
  margin-bottom: 12px;

  &--open {
    background: linear-gradient(
      90deg,
      rgba(46, 204, 113, 0.15),
      rgba(46, 204, 113, 0.05)
    );
    border: 1px solid rgba(46, 204, 113, 0.3);

    .status-banner__dot {
      background: #2ecc71;
      box-shadow: 0 0 6px rgba(46, 204, 113, 0.5);
    }
    .status-banner__content {
      color: #2ecc71;
    }
  }

  &--closed {
    background: linear-gradient(
      90deg,
      rgba(196, 30, 42, 0.15),
      rgba(196, 30, 42, 0.05)
    );
    border: 1px solid rgba(196, 30, 42, 0.3);

    .status-banner__dot {
      background: #c41e2a;
      box-shadow: 0 0 6px rgba(196, 30, 42, 0.5);
    }
    .status-banner__content {
      color: #e8343f;
    }
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__content {
    font-size: 13px;
    font-weight: 500;
  }

  &__plz {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }

  &--closed &__plz-btn {
    background: rgba(196, 30, 42, 0.2);
    color: #e8343f;

    &:hover {
      background: rgba(196, 30, 42, 0.3);
    }
  }

  &__plz-btn {
    background: rgba(46, 204, 113, 0.2);
    color: #2ecc71;
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    margin-left: auto;

    &:hover {
      background: rgba(46, 204, 113, 0.3);
    }

    @media (max-width: 576px) {
      margin-left: 0;
    }
  }
}

.carousel {
  margin-bottom: 16px;

  &__track {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    gap: 12px;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__slide {
    flex: 0 0 100%;
    scroll-snap-align: start;
  }

  &__arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #fff;
    z-index: 2;
    transition: background 0.2s;

    &:hover {
      background: rgba(0, 0, 0, 0.7);
    }
    &--right {
      right: 12px;
    }
  }

  &__dots {
    display: flex;
    justify-content: center;
    gap: 6px;
    margin-top: 10px;
  }

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 3px;
    background: rgba(102, 102, 102, 0.3);
    cursor: pointer;
    transition: all 0.2s;

    &.active {
      width: 20px;
      background: #c41e2a;
    }
  }
}

.promo-slide {
  position: relative;
  background: linear-gradient(135deg, #c41e2a, #8b1520);
  border-radius: 20px;
  height: 155px;
  overflow: hidden;

  &__circles {
    position: absolute;
    inset: 0;
  }
  &__circle {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    &--1 {
      width: 120px;
      height: 120px;
      top: -20px;
      right: -20px;
    }
    &--2 {
      width: 80px;
      height: 80px;
      bottom: -30px;
      right: 30px;
      background: rgba(255, 255, 255, 0.06);
    }
  }

  &__content {
    position: relative;
    z-index: 1;
    padding: 24px;
  }

  &__badge {
    display: inline-block;
    background: rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.7);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 4px 12px;
    border-radius: 20px;
    margin-bottom: 10px;
  }

  &__title {
    color: #fff;
    font-size: 22px;
    font-weight: 800;
    line-height: 1.2;
  }

  &__emoji {
    position: absolute;
    right: 16px;
    bottom: 12px;
    font-size: 52px;
  }
}

.bs-slide {
  position: relative;
  background: var(--pok-card);
  border-radius: 20px;
  height: 155px;
  overflow: hidden;
  cursor: pointer;

  &__img {
    position: absolute;
    right: -10px;
    top: -10px;
    bottom: -10px;
    width: 50%;
    background-size: cover;
    background-position: center;
  }

  &__gradient {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to right,
      var(--pok-card) 0%,
      var(--pok-card) 45%,
      rgba(30, 30, 30, 0.85) 70%,
      rgba(30, 30, 30, 0.3) 100%
    );
  }

  &__content {
    position: relative;
    z-index: 1;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
  }

  &__badge {
    display: inline-block;
    background: rgba(196, 30, 42, 0.15);
    color: #c41e2a;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 1px;
    padding: 3px 8px;
    border-radius: 8px;
    margin-bottom: 8px;
    width: fit-content;
  }

  &__title {
    color: var(--pok-text);
    font-size: 20px;
    font-weight: 800;
    margin-bottom: 4px;
  }

  &__sub {
    color: var(--pok-text-muted);
    font-size: 12px;
    margin-bottom: 10px;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__price {
    color: #c41e2a;
    font-size: 18px;
    font-weight: 800;
  }

  &__btn {
    background: #c41e2a;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    padding: 8px 14px;
    border-radius: 10px;
  }
}

.plz-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 51000;
}

.plz-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  height: 100dvh;
  height: 100vh;

  @supports (height: 100dvh) {
    height: 100dvh;
  }

  background: var(--pok-bg);
  z-index: 51001;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.5);

  &--mobile {
    width: 100vw;
  }
}

.auth-input {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--pok-card);
  border: 1px solid var(--pok-border);
  border-radius: 12px;
  padding: 14px 16px;
  color: var(--pok-text-muted);

  &:focus-within {
    border-color: #c41e2a;
    color: #c41e2a;
  }

  input {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    color: var(--pok-text);
    font-size: 15px;
    &::placeholder {
      color: var(--pok-text-muted);
    }
  }
}

@media (max-width: 576px) {
  .plz-popup .vs-popup {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    margin: 0 !important;
    border-radius: 0 !important;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.8;
  }
}

.cart-qty-input .vs-input-number--button-plus,
.cart-qty-input .vs-input-number--button-less {
  width: 40px !important;
  height: 40px !important;
  font-size: 22px !important;
}
.cart-qty-input .vs-input-number--input {
  width: 40px !important;
  height: 40px !important;
  font-size: 16px !important;
}
.cart-delete-btn {
  min-width: 40px !important;
  min-height: 40px !important;
  padding: 0 12px !important;
}
.cart-delete-btn .feather-icon svg {
  width: 20px !important;
  height: 20px !important;
}
</style>
